import { useEffect, useRef, useState } from 'react';
import type { ObligationKind, ObligationPriority, OccasionType } from '@almanac/shared';
import {
  findExistingOccasion,
  formatTimeForDisplay,
  joinPersonNames,
  newId,
  useCreateObligation,
  useLifeAreas,
  useObligations,
  type RecurrenceEnd,
  withRecurrenceEnd,
} from '../lib/obligations.js';
import { parseTitle } from '../lib/parseTitle.js';
import { usePeople } from '../lib/people.js';
import { PersonPicker } from './PersonPicker.js';
import { ReminderPrompt } from './ReminderPrompt.js';

type RecurrenceMode = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

// Yearly RRULE pinned to a specific month + day. Used in occasion mode
// where the user enters the actual birth/wedding date and we want the
// rule to fire once a year on that calendar day.
function yearlyRuleFromDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `FREQ=YEARLY;BYMONTH=${d.getMonth() + 1};BYMONTHDAY=${d.getDate()}`;
}

function yearFromIso(iso: string): number {
  return Number(iso.slice(0, 4));
}

function formatNoticeDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

function duplicatePersonName(
  people: { id: string; displayName: string }[] | undefined,
  personId: string | undefined,
): string | undefined {
  if (!people || !personId) return undefined;
  return people.find((p) => p.id === personId)?.displayName;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function isoFromDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayIso(): string {
  return isoFromDate(new Date());
}

function buildRecurrence(mode: RecurrenceMode, end: RecurrenceEnd): string | null {
  const base =
    mode === 'daily'
      ? 'FREQ=DAILY'
      : mode === 'weekly'
        ? 'FREQ=WEEKLY'
        : mode === 'monthly'
          ? 'FREQ=MONTHLY'
          : mode === 'yearly'
            ? 'FREQ=YEARLY'
            : null;
  return base ? withRecurrenceEnd(base, end) : null;
}

/** The Ends row inside the Repeats section: Never / After N times /
 *  On date, with the small detail input appearing under the active
 *  choice. Chip styling matches the cadence row above it. */
function EndsControls({
  end,
  onChange,
}: {
  end: RecurrenceEnd;
  onChange: (end: RecurrenceEnd) => void;
}) {
  function defaultUntil(): string {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return isoFromDate(d);
  }
  return (
    <>
      <div className="quick-add__ends-label">Ends</div>
      <div className="quick-add__popover-chips" role="radiogroup" aria-label="Ends">
        <button
          type="button"
          role="radio"
          aria-checked={end.kind === 'never'}
          className={`quick-add__popover-chip ${end.kind === 'never' ? 'quick-add__popover-chip--active' : ''}`}
          onClick={() => onChange({ kind: 'never' })}
        >
          Never
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={end.kind === 'count'}
          className={`quick-add__popover-chip ${end.kind === 'count' ? 'quick-add__popover-chip--active' : ''}`}
          onClick={() => onChange({ kind: 'count', count: end.kind === 'count' ? end.count : 10 })}
        >
          After&hellip;
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={end.kind === 'until'}
          className={`quick-add__popover-chip ${end.kind === 'until' ? 'quick-add__popover-chip--active' : ''}`}
          onClick={() =>
            onChange({ kind: 'until', date: end.kind === 'until' ? end.date : defaultUntil() })
          }
        >
          On date&hellip;
        </button>
      </div>
      {end.kind === 'count' && (
        <label className="quick-add__ends-detail">
          after
          <input
            type="number"
            min={1}
            max={999}
            className="quick-add__time-input quick-add__ends-count"
            value={end.count}
            onChange={(e) =>
              onChange({ kind: 'count', count: Math.max(1, Number(e.target.value) || 1) })
            }
            aria-label="Number of occurrences"
          />
          times
        </label>
      )}
      {end.kind === 'until' && (
        <label className="quick-add__ends-detail">
          last on
          <input
            type="date"
            className="quick-add__time-input"
            value={end.date}
            onChange={(e) =>
              e.target.value && onChange({ kind: 'until', date: e.target.value })
            }
            aria-label="Last occurrence date"
          />
        </label>
      )}
    </>
  );
}

// Compact label for the date pill. iOS's native `<input type="date">`
// rendered the full localised date and made the pill nearly twice the
// width of its siblings; this keeps the row to a single line on a
// phone. Within the next week we show the weekday name; further out
// we fall back to day + short month.
function formatDateForPill(iso: string): string {
  const target = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(target.getTime())) return '—';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff >= 2 && diff <= 6) {
    return target.toLocaleDateString(undefined, { weekday: 'long' });
  }
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (target.getFullYear() !== today.getFullYear()) opts.year = 'numeric';
  return target.toLocaleDateString(undefined, opts);
}

const RECURRENCE_LABEL: Record<RecurrenceMode, string> = {
  none: 'One-off',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
};

interface WhenPillProps {
  value: string;
  onChange: (iso: string) => void;
  /** When provided, the popover also exposes a Repeats section and
   *  the pill label appends the recurrence (e.g. "Today · weekly").
   *  Omitted in occasion mode where recurrence is derived from the
   *  date itself (yearly on month/day). */
  recurrence?: RecurrenceMode;
  onRecurrenceChange?: (mode: RecurrenceMode) => void;
  /** End condition for the chosen cadence — "ends after N times" /
   *  "ends on date". Rendered as a second chip row inside the Repeats
   *  section, only while a cadence is active. */
  recurrenceEnd?: RecurrenceEnd;
  onRecurrenceEndChange?: (end: RecurrenceEnd) => void;
  /** When provided, the popover also exposes a Time row with a
   *  native time input. Stored as `HH:MM`; null means all-day.
   *  Omitted in occasion mode (birthdays are all-day by convention). */
  time?: string | null;
  onTimeChange?: (time: string | null) => void;
}

/**
 * Combined date + recurrence + optional time pill. Opens a popover
 * with date shortcuts (Today, Tomorrow, This weekend, Next week,
 * Pick date…) and, when wired in, a Repeats row and a Time row
 * underneath. All three fields share one pill so the task quick-add
 * row stays at four chips on a phone instead of overflowing.
 *
 * When Repeats or Time are present the popover stays open after each
 * click so the user can set everything in one go; they dismiss with
 * the Done button, Escape, or by tapping outside. In occasion mode
 * (no recurrence, no time) the popover closes on date-click exactly
 * as the old DatePill did.
 */
function WhenPill({
  value,
  onChange,
  recurrence,
  onRecurrenceChange,
  recurrenceEnd,
  onRecurrenceEndChange,
  time,
  onTimeChange,
}: WhenPillProps) {
  const showRepeats = Boolean(onRecurrenceChange);
  const showTime = Boolean(onTimeChange);
  // Popover stays open across selections when there's more than one
  // field to set; with just a date it auto-closes on pick, matching
  // the original DatePill behaviour.
  const stayOpenAcrossPicks = showRepeats || showTime;
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const pickInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (
        target &&
        !buttonRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function offsetIso(days: number): string {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    return isoFromDate(d);
  }

  function weekendIso(): string {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const offset = day === 6 ? 0 : (6 - day + 7) % 7;
    d.setDate(d.getDate() + (offset === 0 && day === 0 ? 6 : offset));
    return isoFromDate(d);
  }

  // Shortcuts compute their ISOs once per render so we can both
  // dispatch them and show a selected state by comparison.
  const todayIsoVal = offsetIso(0);
  const tomorrowIsoVal = offsetIso(1);
  const weekendIsoVal = weekendIso();
  const nextWeekIsoVal = offsetIso(7);

  function pickDate(iso: string) {
    onChange(iso);
    // Auto-close in date-only mode preserves the old DatePill feel.
    // With repeats or a time field exposed the user typically wants
    // to set more than one thing, so the popover stays open until
    // they dismiss it.
    if (!stayOpenAcrossPicks) setOpen(false);
  }

  const timeFragment = showTime && time ? ` ${formatTimeForDisplay(time)}` : '';
  const recurrenceFragment =
    showRepeats && recurrence && recurrence !== 'none'
      ? ` · ${RECURRENCE_LABEL[recurrence].toLowerCase()}`
      : '';
  const pillLabel = `${formatDateForPill(value)}${timeFragment}${recurrenceFragment}`;

  // Build a human-readable aria-label that names every part the
  // pill currently encodes — date, time (when set), recurrence
  // (when not one-off). Screen readers should hear the same data
  // the sighted label conveys.
  const ariaParts: string[] = [`Due ${formatDateForPill(value)}`];
  if (showTime && time) ariaParts.push(`at ${formatTimeForDisplay(time)}`);
  if (showRepeats) {
    ariaParts.push(
      recurrence && recurrence !== 'none'
        ? `repeats ${RECURRENCE_LABEL[recurrence].toLowerCase()}`
        : 'one-off',
    );
  }
  const ariaLabel = ariaParts.join(', ');

  return (
    <div className="quick-add__date">
      <button
        ref={buttonRef}
        type="button"
        className="quick-add__pill quick-add__pill--select"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {pillLabel}
      </button>

      {open && (
        <div ref={popoverRef} className="quick-add__popover" role="menu">
          <Shortcut label="Today" iso={todayIsoVal} current={value} onPick={pickDate} />
          <Shortcut label="Tomorrow" iso={tomorrowIsoVal} current={value} onPick={pickDate} />
          <Shortcut label="This weekend" iso={weekendIsoVal} current={value} onPick={pickDate} />
          <Shortcut label="Next week" iso={nextWeekIsoVal} current={value} onPick={pickDate} />
          {/* "Pick date…" wraps a real native date input that's
              stretched transparently across the whole menu item.
              Two paths, both needed:
              - iOS Safari: the tap must land directly on the input
                element to open the native calendar. The wrapped,
                full-bleed input satisfies that.
              - Desktop Chrome: focus on a date input doesn't open
                the picker; only the calendar icon does — and we've
                hidden that icon. So on click we explicitly call
                `input.showPicker()`, which Chrome 99+ and Safari 16+
                support and which is allowed inside a user gesture.
              Both browsers end up opening the native picker. */}
          <label
            className="quick-add__popover-item quick-add__popover-item--muted quick-add__popover-item--pick"
            onClick={() => {
              const input = pickInputRef.current;
              if (input && typeof input.showPicker === 'function') {
                try {
                  input.showPicker();
                } catch {
                  // Non-user-gesture invocations throw; harmless to swallow.
                }
              }
            }}
          >
            <span>Pick date…</span>
            <input
              ref={pickInputRef}
              type="date"
              value={value}
              onChange={(e) => {
                if (e.target.value) onChange(e.target.value);
                if (!stayOpenAcrossPicks) setOpen(false);
              }}
              className="quick-add__date-trigger-input"
              aria-label="Pick a specific date"
            />
          </label>

          {showTime && onTimeChange && (
            <CollapsibleSection
              label="Time"
              value={time ? formatTimeForDisplay(time) : 'All day'}
              defaultOpen={Boolean(time)}
            >
              <div className="quick-add__popover-time-row">
                {/* Native time input opens the OS picker on iOS and
                    the wheel/spinner on desktop. `value=""` is what
                    HTML's time input expects to mean "no value"; we
                    convert that back to a null on the way out so
                    the data model carries a clean optional. */}
                <input
                  type="time"
                  className="quick-add__time-input"
                  value={time ?? ''}
                  onChange={(e) => onTimeChange(e.target.value || null)}
                  aria-label="Time of day"
                />
                {time && (
                  <button
                    type="button"
                    className="quick-add__time-clear"
                    onClick={() => onTimeChange(null)}
                  >
                    Clear
                  </button>
                )}
              </div>
              {/* Only nudge once the user has actually pinned a
                  time — that's when reminders go from abstract
                  feature to something they'd reasonably want.
                  The component self-hides if reminders are
                  already on or the user has previously
                  dismissed. */}
              {time && <ReminderPrompt />}
            </CollapsibleSection>
          )}

          {showRepeats && onRecurrenceChange && (
            <CollapsibleSection
              label="Repeats"
              value={RECURRENCE_LABEL[recurrence ?? 'none']}
              defaultOpen={Boolean(recurrence && recurrence !== 'none')}
            >
              <div className="quick-add__popover-chips" role="radiogroup" aria-label="Repeats">
                {(Object.keys(RECURRENCE_LABEL) as RecurrenceMode[]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={recurrence === mode}
                    className={`quick-add__popover-chip ${recurrence === mode ? 'quick-add__popover-chip--active' : ''}`}
                    onClick={() => onRecurrenceChange(mode)}
                  >
                    {RECURRENCE_LABEL[mode]}
                  </button>
                ))}
              </div>
              {recurrence && recurrence !== 'none' && onRecurrenceEndChange && (
                <EndsControls end={recurrenceEnd ?? { kind: 'never' }} onChange={onRecurrenceEndChange} />
              )}
            </CollapsibleSection>
          )}

          {stayOpenAcrossPicks && (
            <button
              type="button"
              className="quick-add__popover-done"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible section inside the When popover. Each section's
 * header is a tappable button showing the section name on the left,
 * the current value on the right, and a chevron that rotates with
 * the open state. Keeps the popover compact in landscape — the four
 * date shortcuts plus two collapsed sections (Time + Repeats) fit
 * comfortably; expanding either reveals its controls.
 *
 * `defaultOpen` initialises off whether the section's value is
 * non-default (a set time, a recurring cadence). After mount the
 * user's toggle wins — we don't fight them by re-collapsing if they
 * opened a section to look at it.
 */
function CollapsibleSection({
  label,
  value,
  defaultOpen,
  children,
}: {
  label: string;
  value: string;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <button
        type="button"
        className="quick-add__popover-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="quick-add__popover-toggle-label">{label}</span>
        <span className="quick-add__popover-toggle-value">{value}</span>
        <span className="quick-add__popover-toggle-chevron" aria-hidden>
          ▾
        </span>
      </button>
      {open && children}
    </>
  );
}

function Shortcut({
  label,
  iso,
  current,
  onPick,
}: {
  label: string;
  iso: string;
  current: string;
  onPick: (iso: string) => void;
}) {
  const selected = current === iso;
  return (
    <button
      type="button"
      role="menuitem"
      className={`quick-add__popover-item ${selected ? 'quick-add__popover-item--selected' : ''}`}
      onClick={() => onPick(iso)}
    >
      {label}
    </button>
  );
}

interface PillSelectOption<T extends string> {
  value: T;
  label: string;
}

interface PillSelectProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: PillSelectOption<T>[];
  ariaLabel: string;
}

/**
 * A pill-styled single-select that opens the same custom popover as
 * the When pill, rather than the OS-native `<select>` sheet. Native
 * selects on iOS render as a dark system overlay that visually
 * clashes with the warm beige aesthetic and with the When popover
 * sitting right next to them — so all of Category, Occasion type,
 * and Priority use this component instead. The trigger keeps the
 * pill's chevron and `aria-expanded` semantics; the menu inherits
 * `.quick-add__popover` styling and the selected-row highlight from
 * the When popover.
 */
function PillSelect<T extends string>({ value, onChange, options, ariaLabel }: PillSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (
        target &&
        !buttonRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div className="quick-add__date">
      <button
        ref={buttonRef}
        type="button"
        className="quick-add__pill quick-add__pill--select"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {current?.label ?? ''}
      </button>
      {open && (
        <div ref={popoverRef} className="quick-add__popover" role="menu">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="menuitem"
              className={`quick-add__popover-item ${o.value === value ? 'quick-add__popover-item--selected' : ''}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface QuickAddBarProps {
  /** Called when the user follows the "Edit existing" link in the
   *  duplicate-occasion notice. The parent (Obligations page) owns
   *  the edit drawer and opens it by id. */
  onEditExisting?: (id: string) => void;
}

export function QuickAddBar({ onEditExisting }: QuickAddBarProps = {}) {
  const lifeAreas = useLifeAreas();
  const create = useCreateObligation();
  const obligations = useObligations();
  const people = usePeople();

  const defaultLifeAreaId =
    lifeAreas.data?.find((a) => a.slug === 'personal')?.id ?? lifeAreas.data?.[0]?.id ?? '';

  const [title, setTitle] = useState('');
  const [lifeAreaId, setLifeAreaId] = useState('');
  const [dueDate, setDueDate] = useState(todayIso);
  const [dueTime, setDueTime] = useState<string | null>(null);
  const [recurrenceMode, setRecurrenceMode] = useState<RecurrenceMode>('none');
  const [recurrenceEnd, setRecurrenceEnd] = useState<RecurrenceEnd>({ kind: 'never' });
  const [priority, setPriority] = useState<ObligationPriority>('medium');
  const [kind, setKind] = useState<ObligationKind>('task');
  const [occasionType, setOccasionType] = useState<OccasionType>('birthday');
  // Single subject for birthdays, possibly several for anniversaries.
  // Both modes feed the same `personIds` array so the picker can flip
  // shape without re-shaping the state.
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [occasionLabel, setOccasionLabel] = useState('');

  // Once the user has manually touched a pill, the title parser
  // stops overwriting it. Without this guard, a user who types
  // "tomorrow", then picks a different date from the When pill,
  // would have their pick stomped on by the next keystroke. The
  // flags reset on form reset so a fresh entry starts parsable
  // again.
  const dueDateTouched = useRef(false);
  const dueTimeTouched = useRef(false);
  const recurrenceTouched = useRef(false);
  const priorityTouched = useRef(false);
  const lifeAreaTouched = useRef(false);

  // The form starts collapsed (title + Add only) and reveals the
  // category/due/repeats/priority pills when any field is focused.
  // Tap outside or submit → collapse again. Defaults are used silently
  // when the pills are never opened.
  const formRef = useRef<HTMLFormElement>(null);
  const [expanded, setExpanded] = useState(false);
  // Refs that stay in sync with state so the document-level listeners
  // (installed once on mount) can read the latest values without
  // re-binding on every render.
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  // Armed by pointerdown-outside-form; the immediately-following click
  // is intercepted in capture phase so the list item underneath the
  // tap doesn't also open its edit drawer.
  const swallowNextClickRef = useRef(false);

  // The life-areas query loads async; seed the select once it arrives.
  useEffect(() => {
    if (!lifeAreaId && defaultLifeAreaId) setLifeAreaId(defaultLifeAreaId);
  }, [defaultLifeAreaId, lifeAreaId]);

  // Close-on-outside-tap and close-on-Escape. Pointer-based dismissal
  // is the right primitive here because picks inside any of our
  // custom popovers (When, Category, Priority, Occasion type) land on
  // DOM we control, so they don't trip the outside-tap handler — the
  // form stays open across pill selections.
  //
  // Listeners are installed once on mount and read `expanded` via a
  // ref. The effect that closes on outside-pointerdown also arms a
  // single follow-up click to be suppressed in capture phase — without
  // that, the click that closed the form would continue on to whatever
  // was tapped (typically a task row), opening its edit drawer.
  // Resets every form input to its default and collapses. Used for
  // Escape and outside-tap, so "cancel" actually means cancel — not
  // "minimize but keep the kind toggled to Occasion with a person
  // half-selected", which previously left the user with no visible
  // way back to a task entry.
  function resetForm() {
    setTitle('');
    setDueDate(todayIso());
    setDueTime(null);
    setRecurrenceMode('none');
    setRecurrenceEnd({ kind: 'never' });
    setPriority('medium');
    setLifeAreaId(defaultLifeAreaId);
    setPersonIds([]);
    setOccasionType('birthday');
    setOccasionLabel('');
    setKind('task');
    setExpanded(false);
    dueDateTouched.current = false;
    dueTimeTouched.current = false;
    recurrenceTouched.current = false;
    priorityTouched.current = false;
    lifeAreaTouched.current = false;
  }

  // ── Title → pills natural-language parsing ────────────
  //
  // As the user types in the title field, run the parser and apply
  // any recognised date / time / recurrence to the matching pill —
  // but only if the user hasn't already manually touched that pill
  // this session. The title input itself stays as the user wrote
  // it; tokens are only stripped on submit, in the submit handler
  // below.
  function handleTitleChange(next: string) {
    setTitle(next);
    if (kind !== 'task') return;
    // Occasion mode has its own date semantics (anchor year, yearly
    // recurrence derived from the date) so we skip parsing there —
    // the user picks the birthday/anniversary date with the date
    // pill rather than typing it.
    const parsed = parseTitle(next);
    if (!dueDateTouched.current) {
      setDueDate(parsed.dueDate ?? todayIso());
    }
    if (!dueTimeTouched.current) {
      setDueTime(parsed.dueTime ?? null);
    }
    if (!recurrenceTouched.current) {
      setRecurrenceMode(parsed.recurrence ?? 'none');
    }
    if (!priorityTouched.current) {
      setPriority(parsed.priority ?? 'medium');
    }
    if (!lifeAreaTouched.current && parsed.categorySlug) {
      // Resolve the suggested slug against the loaded life areas;
      // skip if the area doesn't exist (e.g. the user removed it).
      // No keyword match → leave the current life area alone
      // (don't snap back to default mid-typing).
      const match = lifeAreas.data?.find((a) => a.slug === parsed.categorySlug);
      if (match) setLifeAreaId(match.id);
    }
  }

  // Wrapped setters that mark each pill as user-touched so the
  // parser stops driving it. Once touched, the pill is the user's
  // territory for the rest of this draft.
  function handleDueDateChange(next: string) {
    dueDateTouched.current = true;
    setDueDate(next);
  }
  function handleDueTimeChange(next: string | null) {
    dueTimeTouched.current = true;
    setDueTime(next);
  }
  function handleRecurrenceChange(next: RecurrenceMode) {
    recurrenceTouched.current = true;
    setRecurrenceMode(next);
  }
  function handlePriorityChange(next: ObligationPriority) {
    priorityTouched.current = true;
    setPriority(next);
  }
  function handleLifeAreaChange(next: string) {
    lifeAreaTouched.current = true;
    setLifeAreaId(next);
  }

  // Refs that stay in sync with the latest reset so the document-level
  // listeners (installed once on mount) can call it without re-binding.
  const resetFormRef = useRef(resetForm);
  resetFormRef.current = resetForm;

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (!expandedRef.current) return;
      const target = e.target as Node | null;
      if (formRef.current && target && !formRef.current.contains(target)) {
        resetFormRef.current();
        swallowNextClickRef.current = true;
        // Disarm after one paint frame in case no click follows (e.g.
        // a scroll gesture cancels the tap) so we don't swallow an
        // unrelated click later.
        window.setTimeout(() => {
          swallowNextClickRef.current = false;
        }, 350);
      }
    }
    function onClickCapture(e: MouseEvent) {
      if (!swallowNextClickRef.current) return;
      swallowNextClickRef.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && expandedRef.current) resetFormRef.current();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('click', onClickCapture, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('click', onClickCapture, true);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  function handleFormFocus() {
    setExpanded(true);
  }

  function submit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!lifeAreaId) return;
    if (kind === 'occasion') {
      if (personIds.length === 0) return;
      const names = personIds
        .map((id) => people.data?.find((p) => p.id === id)?.displayName)
        .filter((n): n is string => Boolean(n));
      create.mutate({
        id: newId(),
        title: joinPersonNames(names),
        lifeAreaId,
        dueDate,
        priority: 'medium',
        visibility: 'private',
        kind: 'occasion',
        occasionType,
        occasionLabel:
          occasionType === 'anniversary' && occasionLabel.trim()
            ? occasionLabel.trim()
            : null,
        anchorYear: yearFromIso(dueDate),
        personIds,
        recurrenceRule: yearlyRuleFromDate(dueDate),
      });
    } else {
      // Strip recognised date/time/recurrence tokens from the
      // saved title — "Buy milk tomorrow at 3pm" saves as just
      // "Buy milk". The pill values already reflect the parsed
      // tokens (set by handleTitleChange as the user typed),
      // unless the user manually overrode them. If the entire
      // title was a date phrase (e.g. just "tomorrow"), the
      // stripped result is empty — guard against that.
      const stripped = parseTitle(title).title;
      const trimmed = stripped.trim();
      if (!trimmed) return;
      const recurrenceRule = buildRecurrence(recurrenceMode, recurrenceEnd);
      create.mutate({
        id: newId(),
        title: trimmed,
        lifeAreaId,
        dueDate,
        dueTime,
        priority,
        visibility: 'private',
        kind: 'task',
        ...(recurrenceRule ? { recurrenceRule } : {}),
      });
    }
    resetForm();
    (document.activeElement as HTMLElement | null)?.blur();
  }

  // Duplicate guard only for birthdays — anniversaries can have
  // several legitimate entries per person (wedding + first-met +
  // arrival).
  const duplicate =
    kind === 'occasion' && occasionType === 'birthday'
      ? findExistingOccasion(obligations.data, personIds[0] ?? null, 'birthday')
      : null;

  const disabled =
    !lifeAreaId ||
    (kind === 'task' ? !title.trim() : personIds.length === 0) ||
    duplicate !== null;

  return (
    <form
      ref={formRef}
      onSubmit={submit}
      onFocus={handleFormFocus}
      className="quick-add"
      aria-label="Add obligation"
    >
      <div className="quick-add__row">
        {kind === 'task' ? (
          <input
            className="quick-add__input"
            type="text"
            name="quick-add-title"
            autoComplete="off"
            placeholder="What needs doing?"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            aria-label="Obligation title"
          />
        ) : (
          <div className="quick-add__input-slot">
            {/* Birthdays have one subject; anniversaries usually have
                two. The picker switches between single- and multi-
                select based on occasion type. Generic placeholder
                covers both. */}
            {occasionType === 'anniversary' ? (
              <PersonPicker
                multi
                values={personIds}
                onValuesChange={setPersonIds}
              />
            ) : (
              <PersonPicker
                value={personIds[0] ?? null}
                onChange={(id) => setPersonIds(id ? [id] : [])}
              />
            )}
          </div>
        )}
        <button type="submit" className="quick-add__submit" disabled={disabled}>
          Add
        </button>
      </div>

      {duplicate && expanded && (
        <div className="quick-add__notice" role="status">
          <span>
            {duplicatePersonName(people.data, personIds[0]) ?? 'They'} already has a
            birthday on {formatNoticeDate(duplicate.dueDate)}.
          </span>
          {onEditExisting && (
            <button
              type="button"
              className="quick-add__notice-link"
              onClick={() => onEditExisting(duplicate.id)}
            >
              Edit it →
            </button>
          )}
        </div>
      )}

      {/* Anniversary label appears under the input row when relevant.
          Out of the pill row because it benefits from typing space
          (Wedding / partnership / first met …) — a pill-sized field
          would feel cramped. */}
      {kind === 'occasion' && occasionType === 'anniversary' && expanded && (
        <input
          className="quick-add__input quick-add__input--secondary"
          type="text"
          name="occasion-label"
          autoComplete="off"
          placeholder="What is it? Wedding, partnership, first met…"
          value={occasionLabel}
          onChange={(e) => setOccasionLabel(e.target.value)}
          aria-label="What is this anniversary"
        />
      )}

      <div
        className={`quick-add__meta ${expanded ? '' : 'quick-add__meta--collapsed'}`}
        aria-hidden={!expanded}
      >
        {/* Kind toggle sits at the head of the pill row so the rest of
            the pills can switch shape based on it. */}
        <div className="quick-add__kind-toggle" role="tablist" aria-label="Entry kind">
          <button
            type="button"
            role="tab"
            aria-selected={kind === 'task'}
            className={`quick-add__pill quick-add__pill--toggle ${kind === 'task' ? 'quick-add__pill--active' : ''}`}
            onClick={() => setKind('task')}
          >
            Task
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={kind === 'occasion'}
            className={`quick-add__pill quick-add__pill--toggle ${kind === 'occasion' ? 'quick-add__pill--active' : ''}`}
            onClick={() => setKind('occasion')}
          >
            Occasion
          </button>
        </div>

        {/* Slot #2: life area in task mode, occasion type in occasion
            mode. Keeping the position stable means flipping the kind
            toggle doesn't shuffle the row visually — the same slot
            just changes its meaning, which is what the user expects. */}
        {kind === 'task' ? (
          <PillSelect
            value={lifeAreaId}
            onChange={handleLifeAreaChange}
            options={(lifeAreas.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
            ariaLabel="Category"
          />
        ) : (
          <PillSelect<OccasionType>
            value={occasionType}
            onChange={setOccasionType}
            options={[
              { value: 'birthday', label: 'Birthday' },
              { value: 'anniversary', label: 'Anniversary' },
            ]}
            ariaLabel="Occasion type"
          />
        )}

        {/* In task mode the When pill carries both due date and
            recurrence so the row fits on a single line on a phone.
            Occasion mode keeps the date-only pill — occasions repeat
            yearly by definition (from the date itself), and surfacing
            a Repeats picker there would only invite confusion. */}
        {kind === 'task' ? (
          <WhenPill
            value={dueDate}
            onChange={handleDueDateChange}
            recurrence={recurrenceMode}
            onRecurrenceChange={handleRecurrenceChange}
            recurrenceEnd={recurrenceEnd}
            onRecurrenceEndChange={setRecurrenceEnd}
            time={dueTime}
            onTimeChange={handleDueTimeChange}
          />
        ) : (
          <WhenPill value={dueDate} onChange={setDueDate} />
        )}

        {kind === 'task' && (
          <PillSelect<ObligationPriority>
            value={priority}
            onChange={handlePriorityChange}
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
            ]}
            ariaLabel="Priority"
          />
        )}
      </div>
    </form>
  );
}
